"use client";

import React from "react";
import {
  Grid,
  GridContent,
  GridItem,
  GridLoadMore,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { TextCard } from "@/components/TextCard/TextCard";
import type { TextCardElement } from "@/components/TextCard/TextCard";
import GridCategoryFilter from "@/components/GridCategoryFilter/GridCategoryFilter.client";
import buttonStyles from "@/components/Button/Button.module.scss";
import {
  cardGridSources,
  type CardGridLayout,
  type CardGridSettings,
  type CardGridSourceKey,
} from "@/getData/cardGridSource";
import { fetchCardGridPosts } from "@/getData/fetchCardGridPosts";
import type { CategoryOverviewPages } from "@/getData/getPostGridData";
import { useAnimeKeyWithDep } from "@/hooks/useAnimeKey";
import styles from "./CardGrid.module.scss";

interface CardGridClientProps {
  sourceKey: CardGridSourceKey;
  layout: CardGridLayout;
  settings: CardGridSettings;
  cardHeadlineLevel: number;
  initialPosts: TextCardElement[];
  initialTotalPosts: number;
  categories: CategoryOverviewPages[];
}

{
  /* jscpd:ignore-start */
}
const CardGridClient = ({
  sourceKey,
  layout,
  settings,
  cardHeadlineLevel,
  initialPosts,
  initialTotalPosts,
  categories,
}: CardGridClientProps) => {
  const { locale } = useGlobalContext();
  const [activeCategoryId, setActiveCategoryId] = React.useState<number | null>(
    null,
  );
  const [isCategoryChangePending, setIsCategoryChangePending] =
    React.useState(false);
  const source = cardGridSources[sourceKey];
  const { id, posts_per_page, grid_layout } = layout;
  const { show_filter } = settings;
  const showCategoryFilter = show_filter && categories.length > 1;
  const loadingItemsCount = posts_per_page || initialPosts.length || 1;
  const scrollTargetSelector = id ? `#${id}` : undefined;
  const refreshKey = useAnimeKeyWithDep({
    ext: source.animeKeyExt,
    deps: [activeCategoryId],
  });

  const handleCategoryChange = (categoryId: number | null) => {
    setIsCategoryChangePending(true);
    setActiveCategoryId(categoryId);
  };

  return (
    <Grid
      postsPerPage={posts_per_page}
      initialItems={initialPosts}
      initialTotalPosts={initialTotalPosts}
      refreshKey={refreshKey}
      fetchItems={async ({ page, postsPerPage, offset, abortController }) => {
        try {
          return await fetchCardGridPosts({
            page,
            postsPerPage,
            offset,
            locale,
            source,
            settings,
            abortController,
            categoryOverride: activeCategoryId ? [activeCategoryId] : undefined,
          });
        } finally {
          setIsCategoryChangePending(false);
        }
      }}
      renderLoading={{ numberOfItemsWhileLoading: loadingItemsCount }}
    >
      {(gridPosts, loading) => {
        const isLoading = loading || isCategoryChangePending;
        const visiblePosts = isLoading
          ? Array<TextCardElement | null>(loadingItemsCount).fill(null)
          : gridPosts;

        return (
          <>
            {showCategoryFilter && (
              <GridCategoryFilter
                categories={categories}
                activeCategoryId={activeCategoryId}
                onCategoryChange={handleCategoryChange}
              />
            )}
            <div
              className={`${styles["card-grid-content"]} ${styles[`is-${grid_layout}`]}`}
              aria-busy={isLoading}
            >
              <GridContent justifyContent="center" gap="var(--card-grid-gap)">
                {visiblePosts.map((element, key) => {
                  return (
                    <GridItem
                      className={styles["card-grid-item"]}
                      itemWidth={{
                        mobile: "100%",
                        tablet: "33.333333%",
                      }}
                      key={key}
                    >
                      <TextCard
                        skeleton={isLoading}
                        element={
                          isLoading || !element ? source.skeletonCard : element
                        }
                        headingLevel={cardHeadlineLevel}
                      />
                    </GridItem>
                  );
                })}
              </GridContent>
            </div>
            <GridLoadMore
              grid_navigation="only-load-more"
              buttonStyles={buttonStyles}
              iconPosition="left"
              scrollTargetSelector={scrollTargetSelector}
            />
          </>
        );
      }}
    </Grid>
  );
};
{
  /* jscpd:ignore-end */
}

export default CardGridClient;
